/**
 * Service Worker Registration
 * Handles PWA installation and service worker lifecycle
 */

let deferredPrompt: any = null;

/**
 * Check if running in Capacitor native app
 */
function isCapacitorNative(): boolean {
  return typeof (window as any).Capacitor !== 'undefined' && 
         (window as any).Capacitor.isNativePlatform?.() === true;
}

/**
 * Register service worker
 */
export async function registerSW(): Promise<void> {
  // Disable Service Worker in Capacitor native apps - causes white screen
  if (isCapacitorNative()) {
    console.log('[SW] Service Worker disabled in Capacitor native app');
    return;
  }

  if (!('serviceWorker' in navigator)) {
    console.log('[SW] Service workers not supported');
    return;
  }

  // Skip + cleanup inside Lovable preview iframes / preview hosts to avoid stale builds
  const isInIframe = (() => {
    try { return window.self !== window.top; } catch { return true; }
  })();
  const host = window.location.hostname;
  const isPreviewHost =
    host.includes('id-preview--') ||
    host.includes('lovableproject.com') ||
    host.includes('lovable.app');

  if (isPreviewHost || isInIframe) {
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
      if ('caches' in window) {
        const names = await caches.keys();
        await Promise.all(names.map((n) => caches.delete(n)));
      }
      console.log('[SW] Skipped + cleaned in preview/iframe context');
    } catch (e) {
      console.warn('[SW] Preview cleanup failed:', e);
    }
    return;
  }

  try {
    // Versioned SW URL ensures each new build registers as a NEW service worker,
    // forcing install + activate and evicting the previous cached shell.
    // __APP_VERSION__ is injected by vite.config.ts.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const v = (typeof (globalThis as any).__APP_VERSION__ !== 'undefined')
      ? (globalThis as any).__APP_VERSION__
      : 'dev';
    const registration = await navigator.serviceWorker.register(`/sw.js?v=${encodeURIComponent(v)}`, {
      scope: '/',
      updateViaCache: 'none'
    });

    console.log('[SW] Registered:', registration.scope, 'version=', v);

    // Handle updates
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      
      if (!newWorker) return;

      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          // New version available
          console.log('[SW] New version available');
          
          // Dispatch event for UI
          window.dispatchEvent(new CustomEvent('sw-update', {
            detail: { registration }
          }));
        }
      });
    });

    // Listen for messages from SW
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data === 'SYNC_BOOKINGS') {
        window.dispatchEvent(new CustomEvent('sw-sync-bookings'));
      }
      if (event.data === 'SYNC_PROFILE') {
        window.dispatchEvent(new CustomEvent('sw-sync-profile'));
      }
    });

  } catch (error) {
    console.error('[SW] Registration failed:', error);
  }
}

/**
 * Unregister service worker
 */
export async function unregisterSW(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;

  const registration = await navigator.serviceWorker.ready;
  await registration.unregister();
  console.log('[SW] Unregistered');
}

/**
 * Update service worker
 */
export async function updateSW(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;

  const registration = await navigator.serviceWorker.ready;
  await registration.update();
  
  // Skip waiting to activate immediately
  if (registration.waiting) {
    registration.waiting.postMessage('SKIP_WAITING');
  }
}

/**
 * Check if app is installable (PWA)
 */
export function isInstallable(): boolean {
  // PWA install not available in Capacitor native apps
  if (isCapacitorNative()) return false;
  return !!deferredPrompt;
}

/**
 * Install PWA
 */
export async function installPWA(): Promise<boolean> {
  if (!deferredPrompt) {
    return false;
  }

  deferredPrompt.prompt();
  
  const { outcome } = await deferredPrompt.userChoice;
  
  deferredPrompt = null;
  
  return outcome === 'accepted';
}

/**
 * Listen for PWA install prompt
 */
export function listenForInstallPrompt(): () => void {
  // PWA install prompt not available in Capacitor native apps
  if (isCapacitorNative()) {
    return () => {};
  }

  const handler = (e: Event) => {
    e.preventDefault();
    deferredPrompt = e;
    
    // Dispatch event for UI to show install button
    window.dispatchEvent(new CustomEvent('pwa-installable'));
  };

  window.addEventListener('beforeinstallprompt', handler);

  // Listen for app installed
  window.addEventListener('appinstalled', () => {
    console.log('[PWA] App installed');
    deferredPrompt = null;
    window.dispatchEvent(new CustomEvent('pwa-installed'));
  });

  return () => window.removeEventListener('beforeinstallprompt', handler);
}

/**
 * Check if running as installed PWA
 */
export function isRunningAsPWA(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches ||
         (window.navigator as any).standalone === true;
}

/**
 * Check online status
 */
export function isOnline(): boolean {
  return navigator.onLine;
}

/**
 * Listen for online/offline events
 */
export function listenForConnectivity(
  onOnline: () => void,
  onOffline: () => void
): () => void {
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);

  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
  };
}

/**
 * Get network connection info
 */
export function getConnectionInfo(): { 
  effectiveType: string; 
  downlink: number; 
  rtt: number;
  saveData: boolean;
} {
  const connection = (navigator as any).connection ||
                    (navigator as any).mozConnection ||
                    (navigator as any).webkitConnection;

  if (connection) {
    return {
      effectiveType: connection.effectiveType || 'unknown',
      downlink: connection.downlink || 0,
      rtt: connection.rtt || 0,
      saveData: connection.saveData || false
    };
  }

  return {
    effectiveType: 'unknown',
    downlink: 0,
    rtt: 0,
    saveData: false
  };
}

/**
 * Request background sync
 */
export async function requestBackgroundSync(tag: string): Promise<void> {
  if (!('serviceWorker' in navigator) || !('SyncManager' in window)) {
    console.log('[SW] Background sync not supported');
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    await (registration as any).sync.register(tag);
    console.log('[SW] Background sync registered:', tag);
  } catch (error) {
    console.error('[SW] Background sync failed:', error);
  }
}
